import { Resources } from 'constants/resources';

import { archiveCompany } from 'config/requests/companies';
import { useGoBack } from 'hooks/useGoBack';
import { useStagedDelete } from 'hooks/useStagedDelete';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { CancelButton } from 'shared/mui/Buttons';
import { CardHeader } from 'shared/mui/Card';
import { Dialog } from 'shared/mui/Dialogs';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { DeleteStageDialogForm } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogForm';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { Company } from 'types/embedded/company';

import { StyledBox } from './CompaniesArchiveDialog.styled';

type CompaniesArchiveDialogProps = {
  record?: Company;
  open: boolean;
  onClose: VoidFunction;
  isToRootOnSuccess?: boolean;
};

export const CompaniesArchiveDialog: React.FC<CompaniesArchiveDialogProps> = ({
  record,
  open,
  onClose,
  isToRootOnSuccess,
}) => {
  const translate = useTypedTranslate();
  const { goBack } = useGoBack();
  const { deleteOneRecord, denyDeleteReason, stage } = useStagedDelete({
    actionFn: (id: string) => archiveCompany(id, true),
    record,
    onSuccess: () => {
      onClose();
      isToRootOnSuccess && goBack();
      queryClient.refetchQueries({ queryKey: [Resources.Companies] });
    },
  });

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <StyledBox>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.companies.companyArchiveTitle')}
          titleSize="smallBold"
        />
        <DeleteStageDialogForm
          deleteLabel={translate('app.buttons.inArchive')}
          deleteOneRecord={deleteOneRecord}
          details={
            <Typography>
              {translate('app.companies.companyArchiveDetails1')}{' '}
              <TypographyBold fontSize={16} text={record?.name || ''} />{' '}
              {translate('app.companies.companyArchiveDetails2')}
            </Typography>
          }
          onClose={onClose}
          open={true}
          stage={stage}
        >
          <DeleteStageDialogDenyReason reason={denyDeleteReason}>
            <CancelButton
              label={translate('app.companies.returnToList')}
              onClick={isToRootOnSuccess ? goBack : onClose}
              sx={{ marginLeft: 'auto' }}
              variant="outlined"
            />
          </DeleteStageDialogDenyReason>
        </DeleteStageDialogForm>
      </StyledBox>
    </Dialog>
  );
};
